import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IClientPortalNewsService } from "services/ClientPortalNewsService";
import { ClientPortalNewsModel } from "services/api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { RootState } from "setup/redux/RootReducer";

type NewsState = {
  Loading: boolean;
  LatestNews: ClientPortalNewsModel | undefined;
  News: ClientPortalNewsModel[] | undefined;
  CanLoadMore: boolean;
  PageSize: number;
};

const initialState: NewsState = {
  Loading: false,
  LatestNews: undefined,
  News: undefined,
  CanLoadMore: false,
  PageSize: 10,
};

const newsSlice = createSlice({
  name: "news",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLatestNews.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getLatestNews.fulfilled, (state, action) => {
        state.LatestNews = action.payload;
        state.Loading = false;
      })
      .addCase(getLatestNews.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(getNews.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getNews.fulfilled, (state, action) => {
        state.News = action.payload;
        state.News.sort((a, b) => {
          return a.postedOn! < b.postedOn! ? 1 : -1;
        });

        if (action.payload.length < 10) {
          state.CanLoadMore = false;
        } else {
          state.CanLoadMore = true;
        }

        state.Loading = false;
      })
      .addCase(getNews.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(getMoreNews.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getMoreNews.fulfilled, (state, action) => {
        state.News!.push(...action.payload);
        state.News!.sort((a, b) => {
          return a.postedOn! < b.postedOn! ? 1 : -1;
        });

        if (action.payload.length < 10) {
          state.CanLoadMore = false;
        } else {
          state.CanLoadMore = true;
        }

        state.Loading = false;
      })
      .addCase(getMoreNews.rejected, (state) => {
        state.Loading = false;
      });
  },
});

export const getLatestNews = createAsyncThunk<ClientPortalNewsModel>("News/GetLatestNews", async () => {
  const newsService = container.get<IClientPortalNewsService>(SERVICE_TYPES.CPLClientPortalNewsService);
  return await newsService.getLatestNews();
});

export const getMoreNews = createAsyncThunk<
  ClientPortalNewsModel[],
  { index: number | undefined; pageSize: number | undefined }
>("News/GetMoreNews", async ({ index, pageSize }) => {
  const newsService = container.get<IClientPortalNewsService>(SERVICE_TYPES.CPLClientPortalNewsService);
  return await newsService.getNews(index, pageSize);
});

export const getNews = createAsyncThunk<ClientPortalNewsModel[], { pageSize: number }>(
  "News/GetNews",
  async ({ pageSize }) => {
    const newsService = container.get<IClientPortalNewsService>(SERVICE_TYPES.CPLClientPortalNewsService);
    return await newsService.getNews(0, pageSize);
  }
);

export const selectLatestNews = (state: RootState) => state.news.LatestNews;
export const selectNews = (state: RootState) => state.news.News;
export const selectLoading = (state: RootState) => state.news.Loading;
export const selectCanLoadMore = (state: RootState) => state.news.CanLoadMore;
export const selectPageSize = (state: RootState) => state.news.PageSize;

export default newsSlice.reducer;
